import React from 'react';
import { Chip } from '@heroui/react';

/**
 * CredentialSection — one category block of the Credentials page.
 *
 * The page is a single scroll: Documents, Certificates, Endorsements and the
 * FAA Flight Review sit one under another, each in its own framed block with
 * its title, its count and its own primary action. There is no tab switching —
 * that was the point of the merge.
 *
 * `id` doubles as the anchor target for the "On this page" rail, hence
 * `scroll-mt-*`: without it a jump would tuck the heading under the sticky
 * site header.
 *
 * Headings are wrapped in a `<span>` because legacy `frontend.css` forces
 * `h2 { font-size: 30px !important }`; the page CSS neutralises the tag and
 * the span carries the real size (see `user-profile-base.css`).
 */
function CredentialSection({ id, icon: Icon, title, description, count, action, children }) {
	return (
		<section
			id={id}
			aria-labelledby={id ? `${id}-title` : undefined}
			className="scroll-mt-24 bg-content1 border border-default-200 rounded-xl p-4 sm:p-5 flex flex-col gap-4"
		>
			<header className="flex flex-wrap items-start justify-between gap-3">
				<div className="flex items-start gap-2.5 min-w-0">
					{Icon ? (
						<Icon
							className="w-[18px] h-[18px] mt-0.5 text-default-500 shrink-0"
							aria-hidden="true"
						/>
					) : null}
					<div className="flex flex-col gap-0.5 min-w-0">
						<h2 id={id ? `${id}-title` : undefined} className="flex items-center gap-2">
							<span className="text-[16px] font-semibold text-foreground">{title}</span>
							{count != null && (
								<Chip size="sm" radius="sm" variant="flat" className="h-5 px-1.5 text-[11px]">
									{count}
								</Chip>
							)}
						</h2>
						{description ? <p className="text-[13px] text-default-500 m-0">{description}</p> : null}
					</div>
				</div>
				{action ? <div className="shrink-0">{action}</div> : null}
			</header>

			{children}
		</section>
	);
}

export default CredentialSection;
